/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Ajax 클래스를 정의한다.
 *
 * @file /src/utils/Ajax.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 10. 14.
 */
import { UUID } from '@utils/UUID';
import { Exceptions } from './Exceptions';
import { Language } from './Language';

export interface Params {
    [key: string]: string | number;
}

export interface Data {
    [key: string]: any;
}

export interface Results {
    success: boolean;
    message?: string;
    total?: number;
    records?: any[];
    data?: Data;
    [key: string]: any;
}

export class Ajax {
    static language: string;
    static fetchs: Map<string, Promise<Results>> = new Map();

    /**
     * 현재페이지의 아이모듈 언어코드를 가져온다. (zh-Hant → zt)
     *
     * @returns 언어코드
     */
    static getLanguage(): string {
        Ajax.language ??= Language.getDefaultLangauge();
        return Ajax.language;
    }

    /**
     * 지정된 시간만큼 자바스크립트를 지연시킨다.
     *
     * @param ms - 지연될 ms
     */
    static async sleep(ms: number): Promise<void> {
        return new Promise((r) => setTimeout(r, ms));
    }

    /**
     * Fetch 요청의 UUID 값을 생성한다.
     *
     * @param method - 요청방식
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param is_retry - 재시도여부
     * @returns uuid
     */
    static #uuid(
        method: string = 'GET',
        url: string,
        params: Params = {},
        data: Data | FormData = {},
        is_retry: boolean | number = true
    ): string {
        let postData = {};
        if (data instanceof FormData) {
            data.forEach((value, key) => {
                if (value instanceof File) {
                    postData[key] = value.name;
                } else {
                    postData[key] = value;
                }
            });
        } else {
            postData = data;
        }

        return UUID.v5(
            UUID.URL_NAMESPACE,
            JSON.stringify({ method: method, url: url, params: params, data: postData, is_retry: is_retry })
        );
    }

    /**
     * 짧은시간내에 동일한 Fetch 요청이 될 경우,
     * 제일 처음 요청된 Fetch 만 수행하고 응답된 데이터를 다른 중복요청한 곳으로 반환한다.
     *
     * @param method - 요청방식
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param is_retry - 재시도여부
     * @returns 동일한 요청의 제일 첫번째 요청
     */
    static async #call(
        method: string = 'GET',
        url: string,
        params: Params = {},
        data: Data | FormData = {},
        is_retry: boolean | number = true
    ): Promise<Results> {
        const uuid = Ajax.#uuid(method, url, params, data, is_retry);
        if (Ajax.fetchs.has(uuid) == true) {
            return Ajax.fetchs.get(uuid);
        }

        Ajax.fetchs.set(uuid, Ajax.#fetch(method, url, params, data, is_retry));
        return Ajax.fetchs.get(uuid);
    }

    /**
     * 실제 Fetch 함수를 실행한다.
     *
     * @param method - 요청방식
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async #fetch(
        method: string = 'GET',
        url: string,
        params: Params = {},
        data: Data | FormData = {},
        is_retry: boolean | number = true
    ): Promise<Results> {
        const uuid = Ajax.#uuid(method, url, params, data, is_retry);

        const requestUrl = new URL(url, location.origin);
        for (const name in params) {
            if (params[name] === null) {
                requestUrl.searchParams.delete(name);
            } else {
                requestUrl.searchParams.set(name, params[name].toString());
            }
        }
        let retry = (is_retry === false ? 10 : is_retry) as number;

        let is_method_fallback = false;
        let httpMethod = method;
        if (method.indexOf('@') === 0) {
            is_method_fallback = true;
            switch (method) {
                case '@PUT':
                    method = 'PUT';
                    httpMethod = 'POST';
                    break;

                case '@PATCH':
                    method = 'PATCH';
                    httpMethod = 'POST';
                    break;

                case '@DELETE':
                    method = 'DELETE';

                    if (data === null) {
                        httpMethod = 'GET';
                    } else {
                        httpMethod = 'POST';
                    }
                    break;
            }
        }

        if (method == 'SYNC') {
            httpMethod = 'GET';
        }

        if (method == 'DELETE' && data !== null) {
            httpMethod = 'POST';
        }

        const headers = {
            'X-Method': method,
            'Accept-Language': Ajax.getLanguage(),
            'Accept': 'application/json',
        };

        let body: string | FormData = null;

        if (httpMethod == 'POST' || httpMethod == 'PUT' || httpMethod == 'PATCH') {
            if (data instanceof FormData) {
                body = data;
            } else {
                headers['Content-Type'] = 'application/json; charset=utf-8';
                body = JSON.stringify(data);
            }
        }

        let results: Results = null;
        try {
            const response: Response = (await fetch(requestUrl.toString(), {
                method: httpMethod,
                headers: headers,
                body: body,
                cache: 'no-store',
                redirect: 'follow',
            }).catch((e: Error) => {
                throw Exceptions.code('FAILED_TO_LOAD_DATA', null, null, null, e);
            })) as Response;

            if (response.ok == false) {
                if (is_method_fallback == false && (response.status == 405 || response.status == 501)) {
                    if (method == 'PUT' || method == 'PATCH' || method == 'DELETE') {
                        Exceptions.warn(
                            '[Ajax]',
                            `Fallback: "${method}" → "@${method}" due to HTTP ${response.status}`
                        );
                        Ajax.fetchs.delete(uuid);
                        return Ajax.#call('@' + method, url, params, data, is_retry);
                    }
                }
            }

            results = (await response.json()) as Results;
            Ajax.fetchs.delete(uuid);
        } catch (e) {
            Ajax.fetchs.delete(uuid);

            if (retry <= 3) {
                await Ajax.sleep(1000);
                return Ajax.#call(method, url, params, data, ++retry);
            } else {
                throw Exceptions.code('FAILED_TO_LOAD_DATA', null, null, null, e);
            }
        }

        if (results?.success !== undefined && results.success !== true) {
            throw Exceptions.code(
                'FAILED_TO_LOAD_DATA',
                null,
                results.message ?? null,
                results.errors ?? null,
                results
            );
        }

        return results;
    }

    /**
     * GET 방식으로 데이터를 가져온다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async get(url: string, params: Params = {}, is_retry: boolean | number = true): Promise<Results> {
        return Ajax.#call('GET', url, params, null, is_retry);
    }

    /**
     * POST 방식으로 데이터를 가져온다.
     * 전송할 데이터는 JSON 방식으로 전송된다.
     *
     * @param url - 요청주소
     * @param data - 전송할 데이터
     * @param params - GET 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async post(
        url: string,
        data: Data | FormData = {},
        params: Params = {},
        is_retry: boolean | number = true
    ): Promise<Results> {
        return Ajax.#call('POST', url, params, data, is_retry);
    }

    /**
     * PATCH 방식으로 데이터를 가져온다.
     * 전송할 데이터는 JSON 방식으로 전송된다.
     *
     * @param url - 요청주소
     * @param data - 전송할 데이터
     * @param params - GET 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async patch(
        url: string,
        data: Data | FormData = {},
        params: Params = {},
        is_retry: boolean | number = true
    ): Promise<Results> {
        return Ajax.#call('PATCH', url, params, data, is_retry);
    }

    /**
     * DELETE 방식으로 데이터를 가져온다.
     * 전송할 데이터는 JSON 방식으로 전송된다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async delete(
        url: string,
        params: Params = {},
        data: Data | FormData = null,
        is_retry: boolean | number = true
    ): Promise<Results> {
        return Ajax.#call('DELETE', url, params, data, is_retry);
    }

    /**
     * SYNC 방식으로 데이터를 가져온다.
     * 전송할 데이터는 JSON 방식으로 전송된다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param is_retry - 재시도여부
     * @returns 요청결과
     */
    static async sync(url: string, params: Params = {}, is_retry: boolean | number = true): Promise<Results> {
        return Ajax.#call('SYNC', url, params, null, is_retry);
    }
}
